import { Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import Colors from "../../global/Colors";

const CircularGradientProgressBar = ({
  children,
  progressPercent = 0,
  pathColor = Colors.sky[100],
  progressColor = Colors.green[400],
  trackWidth = 2,
  gradientColors = [],
  height = "264",
  width = "264",
}) => {
  const [step, setStep] = useState(1);
  const [finalProgress, setFinalProgress] = useState(100);
  const [progress, setProgress] = useState(0);
  const [remainingProgress, setRemainingProgress] = useState(null);
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const [dashOffset, setDashOffset] = useState(circumference);
  const thumbRef = useRef(null);
  const [xCoord, setXCoord] = useState(0);
  const [yCoord, setYCoord] = useState(0);
  const thumbSize = Math.min(Number(width), Number(height)) * 0.08;

  useEffect(() => {
    setStep(progress <= progressPercent ? 1 : -1);
    setRemainingProgress(null);
    setFinalProgress(progressPercent);
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setProgress((prev) => {
        if (prev < Math.round(progressPercent)) {
          setRemainingProgress(null);
          return prev + step;
        } else if (Math.round(progressPercent) < prev) {
          setRemainingProgress(null);
          return prev - step;
        }
        setRemainingProgress(
          step === 1
            ? Math.max(0, 100 - Math.round(progressPercent))
            : progressPercent > finalProgress
              ? prev
              : Math.round(progressPercent)
        );
        clearInterval(intervalId);
        return Math.round(progressPercent);
      });
    }, 20);
    return () => clearInterval(intervalId);
  }, [progressPercent]);

  useEffect(() => {
    const angle = (progress / 100) * 360;
    const thumbX = 50 + radius * Math.cos((angle - 90) * (Math.PI / 180));
    const thumbY = 50 + radius * Math.sin((angle - 90) * (Math.PI / 180));
    setDashOffset(circumference * ((100 - progress) / 100));
    setXCoord(thumbX);
    setYCoord(thumbY);
  }, [progress]);

  return (
    <div
      style={{
        width: `${width}px`,
        height: `${height}px`,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
      }}
    >
      <svg width="100%" height="100%" viewBox="0 0 100 100">
        <defs>
          <linearGradient
            id="progressGradientCircular"
            x1="100"
            y1="100"
            x2="0"
            y2="100"
            gradientUnits="userSpaceOnUse"
          >
            {gradientColors.map((color, idx) => (
              <stop key={idx} offset={`${color.percent}%`} stopColor={color.color} />
            ))}
          </linearGradient>
        </defs>

        <circle
          cx="50"
          cy="50"
          r={radius}
          stroke={pathColor}
          strokeWidth={trackWidth}
          fill={Colors.white}
        />

        <circle
          id="progress-circle"
          cx="50"
          cy="50"
          r={radius}
          stroke={gradientColors.length > 0 ? "url(#progressGradientCircular)" : progressColor}
          strokeWidth={trackWidth + 2}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform="rotate(-90, 50, 50)"
          fill="none"
        />

        <foreignObject width="100%" height="100%">
          <div className="flex h-full w-full flex-col items-center justify-center">
            <span className="text-3xl font-thin text-black">
              {progress}
              <span className="text-sm font-thin text-black">%</span>
            </span>
            {children ? (
              children
            ) : (
              <>
                <Typography className="text-[6px] font-bold text-black">
                  Treatment Completed
                </Typography>
                <Typography
                  className={`${
                    remainingProgress !== null ? "text-gray-400" : "text-white"
                  } text-[5px] font-normal`}
                >
                  {remainingProgress}% Remaining
                </Typography>
              </>
            )}
          </div>
        </foreignObject>
      </svg>

      {progress > 0 && progress < 100 && (
        <div
          ref={thumbRef}
          className="absolute"
          style={{
            top: `${yCoord}%`,
            left: `${xCoord}%`,
            width: `${thumbSize + 20}px`,
            height: `${thumbSize + 20}px`,
            borderRadius: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 5,
          }}
        >
          <svg width="100%" height="100%" viewBox="0 0 100 100" style={{ display: "block" }}>
            <circle
              cx="50"
              cy="50"
              r={40 - (trackWidth + 5) / 2}
              stroke="url(#progressGradientCircular)"
              strokeWidth={trackWidth + 5}
              fill={Colors.white}
            />
          </svg>
        </div>
      )}
    </div>
  );
};

export default CircularGradientProgressBar;
